/**
 * Registry mapping a Coach `tool-{name}` part type to its renderer. The
 * CoachPage timeline iterates message parts and looks up the component
 * here; unknown tools fall back to a small "⚙ name" badge.
 *
 * Plan-era widgets (PlanCard / ActionChecklist / Calendar) were deleted
 * along with the deep-change-plan model. Asset rendering + simple choice
 * widgets remain.
 */

import type { ComponentType } from "react";

import { ArticleCard } from "./ArticleCard";
import { AudioCard } from "./AudioCard";
import { ChoiceButtons } from "@/components/ChoiceButtons";
import { ImageCard } from "./ImageCard";
import { MoodScale } from "./MoodScale";

type Sender = (text: string) => void;

interface ToolPart {
  type: string;
  toolCallId: string;
  state: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  output?: any;
}

const REGISTRY: Record<
  string,
  ComponentType<{ data: unknown; sendMessage: Sender; toolCallId: string }>
> = {
  present_choice: ({ data, sendMessage, toolCallId }) => {
    const d = data as {
      question?: string;
      options?: { id: string; label: string; hint?: string }[];
    };
    if (!d?.options) return null;
    return (
      <ChoiceButtons
        toolCallId={toolCallId}
        question={d.question ?? ""}
        options={d.options}
        onPick={sendMessage}
      />
    );
  },
  present_mood_scale: ({ data, sendMessage }) => {
    const d = data as {
      question?: string;
      options?: { id: string; label: string; value: number }[];
    };
    if (!d?.options) return null;
    return (
      <MoodScale question={d.question ?? ""} options={d.options} onPick={sendMessage} />
    );
  },
  present_audio: ({ data }) => {
    const d = data as { asset_id?: string; intro?: string };
    if (!d?.asset_id) return null;
    return <AudioCard asset_id={d.asset_id} intro={d.intro} />;
  },
  present_image: ({ data }) => {
    const d = data as { asset_id?: string; caption?: string };
    if (!d?.asset_id) return null;
    return <ImageCard asset_id={d.asset_id} caption={d.caption} />;
  },
  present_article: ({ data }) => {
    const d = data as { asset_id?: string };
    if (!d?.asset_id) return null;
    return <ArticleCard asset_id={d.asset_id} />;
  },
};

export function renderToolPart(part: ToolPart, sendMessage: Sender) {
  if (part.state !== "output-available") return null;
  const name = part.type.startsWith("tool-")
    ? part.type.slice("tool-".length)
    : part.type;
  const Cmp = REGISTRY[name];
  if (!Cmp) {
    return <div className="text-xs italic text-neutral-400">⚙ {name}</div>;
  }
  return (
    <Cmp data={part.output} sendMessage={sendMessage} toolCallId={part.toolCallId} />
  );
}
